import {dataTestId} from '@hconnect/uikit'
import {styled, Skeleton} from '@mui/material'
import {useIsMutating} from '@tanstack/react-query'
import * as d3 from 'd3'
import {throttle} from 'lodash'
import {useRef, useLayoutEffect, useMemo, useState, useEffect, memo, cloneElement} from 'react'
import {useTranslation} from 'react-i18next'

import {GANTT_CHART_ITEM_HEIGHT, GANTT_CHART_ROW_HEIGHT} from '../constants'
import {toPixel} from '../helpers/utils'
import {getNumberInsideFrame, isNumberInsideFrame} from '../selectors/numbers'
import {GanttChartItemPosition} from '../types/common'

import {GanttChartItemTooltip, type GanttChartTooltip} from './GanttChartItemTooltip'

const GANT_ITEM_LABEL_BREAKPOINT_L = 120
const GANT_ITEM_LABEL_BREAKPOINT_M = 70
const GANT_ITEM_LABEL_BREAKPOINT_S = 30
const GANT_CELL_MIN_WIDTH = 30

const GANTT_ITEM_RADIUS = 4
const GANTT_ITEM_BORDER_WIDTH = 1

const StyledGantChartItemContainerDiv = styled('div', {
  shouldForwardProp: (propName) => propName !== 'isReadOnly' && propName !== 'displayBoxShadow'
})<{
  isReadOnly: boolean
  displayBoxShadow: boolean
}>(({theme, isReadOnly, displayBoxShadow}) => ({
  position: 'absolute',
  cursor: isReadOnly ? 'auto' : 'pointer',
  pointerEvents: 'auto',
  borderRadius: GANTT_ITEM_RADIUS,
  whiteSpace: 'nowrap',
  border: `${GANTT_ITEM_BORDER_WIDTH}px solid rgba(6, 50, 79, 0.3)`,
  ...theme.typography.body1,
  transition: 'background-color ease-in-out 200ms',
  // Box shadow without overlapping
  '&::before': displayBoxShadow
    ? {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: -1,
        boxShadow: `0px 0px 1px rgba(0, 0, 0, 0.1), 0px ${theme.spacing(0.5)} ${theme.spacing(
          0.5
        )} rgba(0, 0, 0, 0.5);`
      }
    : undefined
}))

const StyledGantChartItemHandleDiv = styled('div', {
  shouldForwardProp: (propName) => propName !== 'isReadOnly'
})<{isReadOnly: boolean}>(({isReadOnly}) => ({
  position: 'absolute',
  top: 0,
  height: '100%',
  zIndex: 1,
  cursor: 'ew-resize',
  display: isReadOnly ? 'none' : 'initial'
}))

export interface GetGanttChartItemPositionParams {
  start: number
  end: number
  deltaX?: number
  fixed?: 'start' | 'end' | 'width'
}

export interface TGanttChartItem {
  id: string
  start: number
  end: number
  label?: React.ReactNode
  height?: number
  background?: React.ReactElement
  startIcon?: React.ReactNode
  endIcon?: React.ReactNode
  shouldDisableStartChange?: boolean
  shouldDisableEndChange?: boolean
  hasComment?: boolean
  tooltip?: GanttChartTooltip
  testPrefix?: string
}

interface GantChartItemProps {
  item: TGanttChartItem
  rowHeight?: number
  rowIndex?: number
  chartStartBoundary: number
  chartEndBoundary: number
  startOfChart: number
  endOfChart: number
  stepWidth: number
  isReadOnly?: boolean
  mutationKey: string
  onItemClick: (value: string) => void
  dragAndDropItemCallback: (item: TGanttChartItem) => Promise<boolean> | boolean
}

function _GantChartItem({
  item,
  rowHeight = GANTT_CHART_ROW_HEIGHT,
  rowIndex = 0,
  chartStartBoundary,
  chartEndBoundary,
  startOfChart,
  endOfChart,
  stepWidth,
  isReadOnly = false,
  mutationKey,
  onItemClick,
  dragAndDropItemCallback
}: GantChartItemProps) {
  const {t} = useTranslation()

  // Check if current item is mutating by mutationKey
  const mutating = useIsMutating({mutationKey: [mutationKey]})
  const isItemLoading = !!mutating
  const shouldDisableItem = isReadOnly || isItemLoading

  const [ganttChartItem, setGanttChartItem] = useState<TGanttChartItem>(item)

  const itemHeight = item.height ?? GANTT_CHART_ITEM_HEIGHT

  const graphItemRef = useRef<HTMLHeadingElement>(null)
  const labelRef = useRef<HTMLHeadingElement>(null)
  const leftHandleRef = useRef<HTMLHeadingElement>(null)
  const rightHandleRef = useRef<HTMLHeadingElement>(null)

  const checkItemIsOnBoundaryFrame = useMemo(() => {
    return getNumberInsideFrame(chartStartBoundary, chartEndBoundary)
  }, [chartStartBoundary, chartEndBoundary])

  const checkItemIsOnChartFrame = useMemo(() => {
    return getNumberInsideFrame(startOfChart, endOfChart)
  }, [startOfChart, endOfChart])

  const checkIsItemInBoundaryFrame = useMemo(() => {
    return isNumberInsideFrame(chartStartBoundary, chartEndBoundary)
  }, [chartStartBoundary, chartEndBoundary])

  const handleGanttItemClick = () => {
    if (!isItemLoading) {
      onItemClick(ganttChartItem.id)
    }
  }

  useEffect(() => {
    setGanttChartItem(item)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepWidth, item.start, item.end])

  // HCP-98144 extract to separate file
  const getItemPosition = useMemo(() => {
    // eslint-disable-next-line complexity
    return ({
      start,
      end,
      deltaX,
      fixed
    }: GetGanttChartItemPositionParams): GanttChartItemPosition => {
      const isStartFixedOrInitialDraw = fixed === 'start' || !deltaX
      const isEndFixedOrInitialDraw = fixed === 'end' || !deltaX
      const isWidthFixed = fixed === 'width'

      // updating start and end of item based on deltaX ignoring chart start and end limitations
      let updatedStart = isStartFixedOrInitialDraw ? start : start + deltaX
      let updatedEnd = isEndFixedOrInitialDraw ? end : end + deltaX

      if (!isWidthFixed && updatedStart >= end) updatedStart = end
      if (!isWidthFixed && updatedEnd <= start) updatedEnd = start

      const startOnBoundaryFrame = checkItemIsOnBoundaryFrame(updatedStart)
      const endOnBoundaryFrame = checkItemIsOnBoundaryFrame(updatedEnd)
      const startOnChartFrame = checkItemIsOnChartFrame(updatedStart)
      const endOnChartFrame = checkItemIsOnChartFrame(updatedEnd)

      const itemWidth = isWidthFixed ? end - start : endOnBoundaryFrame - startOnBoundaryFrame
      const visibleWidth = isWidthFixed ? end - start : endOnChartFrame - startOnChartFrame
      const labelWidth = visibleWidth > 0 ? visibleWidth : itemWidth
      const labelLeft = startOnChartFrame - startOnBoundaryFrame
      const labelOffset = labelLeft > 0 && labelLeft < itemWidth ? labelLeft : 0
      const maxStart = isWidthFixed ? chartEndBoundary - itemWidth : chartEndBoundary

      return {
        itemWidth,
        start: startOnBoundaryFrame > maxStart ? maxStart : startOnBoundaryFrame,
        end: endOnBoundaryFrame,
        labelWidth,
        labelOffset
      }
    }
  }, [checkItemIsOnBoundaryFrame, checkItemIsOnChartFrame, chartEndBoundary])

  /**
   * We still use d3 for drag and drop handling, directly manipulating the DOM elements.
   * Reasoning: d3 uses some kind of normalized DnD abstraction; instead of attaching drag events to individual
   * elements, the drag handlers are attached to the window object and uses custom mousedown/mouseup events to mimic
   * dragging. This is superior to HTML5 draggable attribute; additionally when using React DnD we would have the issue
   * of re-rendering the whole chart during drag events (possible hundreds of events per second).
   *
   * Note that upon dragend events, we update our graph and a re-rendering happens (only once).
   *
   * useLayoutEffect is called synchronously after render(); this is important as all the ref's will have been set and
   * put into our map at this point. We also use it to avoid flickering during the drawing, as our drawing operations
   * will block here (instead of in useEffect which runs async and wouldn't block but cause a lot of repaints)
   */
  // HCP-98144 extract to separate file
  useLayoutEffect(() => {
    const ganttItemBar = graphItemRef.current
    const ganttItemLabel = labelRef.current
    const leftHandle = leftHandleRef.current
    const rightHandle = rightHandleRef.current

    if (!ganttItemBar || !leftHandle || !rightHandle || !ganttItemLabel) {
      return
    }

    const ganttItemD3 = d3.select<Element, unknown>(ganttItemBar)
    const leftHandleD3 = d3.select<Element, unknown>(leftHandle)
    const rightHandleD3 = d3.select<Element, unknown>(rightHandle)
    const labelD3 = d3.select(ganttItemLabel)
    // elements inside label
    const labelTextD3 = labelD3.select('#label_text')
    const labelIconD3 = labelD3.select('#label_icon')
    const notificationD3 = labelD3.select('#comment_notification')

    const throttledHideLabel = throttle(() => labelTextD3.style('display', 'none'))
    const throttledHideLabelIcon = throttle(() => labelIconD3.style('display', 'none'))
    const throttledShowLabel = throttle(() => labelTextD3.style('display', 'block'))
    const throttledShowLabelIcon = throttle(() => labelIconD3.style('display', 'flex'))
    const throttledHideCommentNotification = throttle(() => notificationD3.style('display', 'none'))
    const throttledShowCommentNotification = throttle(() =>
      notificationD3.style('display', 'inherit')
    )

    const drawGanttItem = (position: GanttChartItemPosition): void => {
      ganttItemD3.style('width', toPixel(position.itemWidth)).style('left', toPixel(position.start))
      labelD3
        .style('width', toPixel(position.labelWidth))
        .style('left', toPixel(position.labelOffset))

      if (position.labelWidth < GANT_ITEM_LABEL_BREAKPOINT_L) {
        throttledHideLabel()
      } else {
        throttledShowLabel()
      }

      if (position.labelWidth < GANT_ITEM_LABEL_BREAKPOINT_M && item.hasComment) {
        throttledHideLabelIcon()
      } else {
        throttledShowLabelIcon()
      }

      if (position.labelWidth < GANT_ITEM_LABEL_BREAKPOINT_S) {
        if (item.hasComment) {
          throttledHideCommentNotification()
        } else {
          throttledHideLabelIcon()
        }
      } else {
        if (item.hasComment) {
          throttledShowCommentNotification()
        } else {
          throttledShowLabelIcon()
        }
      }
    }

    const position = getItemPosition({
      start: ganttChartItem.start,
      end: ganttChartItem.end
    })

    drawGanttItem(position)

    // disabling drag and drop handlers if chart is read only
    if (shouldDisableItem) return

    let startX = 0
    let ganttItemStart = 0

    // draw a left selector on the planning item for resizing to the left
    if (!item.shouldDisableStartChange && checkIsItemInBoundaryFrame(ganttChartItem.start)) {
      leftHandleD3.call(
        d3
          .drag()
          .on('start', (event) => {
            event.sourceEvent.stopPropagation()
            startX = event.x
            ganttItemStart = ganttItemBar.getBoundingClientRect().left
          })
          .on('drag', (event) => {
            event.sourceEvent.stopPropagation()
            const ganttItemNewStart = leftHandle.getBoundingClientRect().left
            const newDifference = ganttItemNewStart - ganttItemStart
            const deltaX = event.x - startX + newDifference
            const position = getItemPosition({
              start: ganttChartItem.start,
              end: ganttChartItem.end,
              deltaX,
              fixed: 'end'
            })
            drawGanttItem(position)
          })
          .on('end', async (event) => {
            event.sourceEvent.stopPropagation()
            const ganttItemNewStart = leftHandle.getBoundingClientRect().left
            const newDifference = ganttItemNewStart - ganttItemStart - GANTT_ITEM_BORDER_WIDTH
            const deltaX = event.x - startX + newDifference
            if (deltaX === 0) return

            const oldPosition = getItemPosition({
              start: item.start,
              end: item.end
            })
            const newPosition = getItemPosition({
              start: ganttChartItem.start,
              end: ganttChartItem.end,
              deltaX,
              fixed: 'end'
            })

            const newItem = {...ganttChartItem, start: newPosition.start}
            setGanttChartItem(newItem)

            const isDragAndDropSuccessful = await dragAndDropItemCallback(newItem)
            if (!isDragAndDropSuccessful) {
              drawGanttItem(oldPosition)
              setGanttChartItem(item)
            }
          })
      )
    }

    // draw a right selector on the planning item for resizing to the right
    if (
      !item.shouldDisableEndChange &&
      checkIsItemInBoundaryFrame(ganttChartItem.end) &&
      !(ganttChartItem.end === chartStartBoundary)
    ) {
      rightHandleD3.call(
        d3
          .drag()
          .on('start', (event) => {
            event.sourceEvent.stopPropagation()
            startX = event.x
          })
          .on('drag', (event) => {
            event.sourceEvent.stopPropagation()
            const deltaX = event.x - startX
            const position = getItemPosition({
              start: ganttChartItem.start,
              end: ganttChartItem.end,
              deltaX,
              fixed: 'start'
            })
            drawGanttItem(position)
          })
          .on('end', async (event) => {
            const deltaX = event.x - startX
            if (deltaX === 0) return
            event.sourceEvent.stopPropagation()

            const oldPosition = getItemPosition({
              start: item.start,
              end: item.end
            })
            const newPosition = getItemPosition({
              start: ganttChartItem.start,
              end: ganttChartItem.end,
              deltaX,
              fixed: 'start'
            })

            const newItem = {...ganttChartItem, end: newPosition.end}
            setGanttChartItem(newItem)

            const isDragAndDropSuccessful = await dragAndDropItemCallback(newItem)
            if (!isDragAndDropSuccessful) {
              drawGanttItem(oldPosition)
              setGanttChartItem(item)
            }
          })
      )
    }

    // Drag and drop handlers for the whole planning item to be moved left and right
    if (!(item.shouldDisableStartChange || item.shouldDisableEndChange)) {
      ganttItemD3.call(
        d3
          .drag()
          .on('start', (event) => {
            event.sourceEvent.stopPropagation()
            startX = event.x
          })
          .on('drag', (event) => {
            event.sourceEvent.stopPropagation()
            const deltaX = event.x - startX

            const position = getItemPosition({
              start: ganttChartItem.start,
              end: ganttChartItem.end,
              deltaX,
              fixed: 'width'
            })
            drawGanttItem(position)
          })
          .on('end', async (event) => {
            const deltaX = event.x - startX
            if (Math.abs(deltaX) < 3) return
            event.sourceEvent.stopPropagation()

            const oldPosition = getItemPosition({
              start: item.start,
              end: item.end
            })
            const newPosition = getItemPosition({
              start: ganttChartItem.start,
              end: ganttChartItem.end,
              deltaX,
              fixed: 'width'
            })

            const newItem = {...ganttChartItem, start: newPosition.start, end: newPosition.end}
            setGanttChartItem(newItem)

            const isDragAndDropSuccessful = await dragAndDropItemCallback(newItem)
            if (!isDragAndDropSuccessful) {
              drawGanttItem(oldPosition)
              setGanttChartItem(item)
            }
          })
      )
    }
  }, [
    item,
    ganttChartItem,
    chartStartBoundary,
    chartEndBoundary,
    shouldDisableItem,
    t,
    dragAndDropItemCallback,
    checkIsItemInBoundaryFrame,
    checkItemIsOnChartFrame,
    getItemPosition,
    checkItemIsOnBoundaryFrame
  ])

  const ItemContent = (
    <>
      {/* Left label */}
      <div style={{position: 'absolute', left: -1, bottom: 15}}>{item.startIcon}</div>

      {/* Left handle */}
      <StyledGantChartItemHandleDiv
        isReadOnly={shouldDisableItem || (item.shouldDisableStartChange ?? false)}
        ref={leftHandleRef}
        style={{
          width: GANT_CELL_MIN_WIDTH,
          left: 0
        }}
        {...dataTestId(`gantt-item-${item.testPrefix ?? ''}${ganttChartItem.id}-left-handle`)}
      />

      {/* Label */}
      <div ref={labelRef}>{item.label}</div>

      {/* Right label */}
      <div style={{position: 'absolute', right: -1, top: 17}}>{item.endIcon}</div>

      {/* Right handle */}
      <StyledGantChartItemHandleDiv
        isReadOnly={shouldDisableItem || (item.shouldDisableEndChange ?? false)}
        ref={rightHandleRef}
        style={{
          width: GANT_CELL_MIN_WIDTH,
          right: 0
        }}
        {...dataTestId(`gantt-item-${item.testPrefix ?? ''}${ganttChartItem.id}-right-handle`)}
      />

      {/* Skeleton loader */}
      {isItemLoading && (
        <Skeleton
          variant="rectangular"
          animation="wave"
          sx={{position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, height: 1}}
        />
      )}
    </>
  )

  return (
    <>
      <StyledGantChartItemContainerDiv
        isReadOnly={shouldDisableItem}
        displayBoxShadow={false}
        ref={graphItemRef}
        style={{
          top: rowHeight * rowIndex + (rowHeight - itemHeight) / 2,
          height: itemHeight
        }}
        onClick={handleGanttItemClick}
        {...dataTestId(`gantt-item-${item.testPrefix ?? ''}${ganttChartItem.id}`)}
      >
        {cloneElement(item.background ? item.background : <></>, {}, ItemContent)}
      </StyledGantChartItemContainerDiv>
      {item.tooltip && <GanttChartItemTooltip anchorElRef={graphItemRef} {...item.tooltip} />}
    </>
  )
}
export const GanttChartItem = memo(_GantChartItem)
